import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';

/**
 * Enterprise-grade hook for Forms translations with type safety and error handling
 * Provides centralized access to all Forms-related translations with performance optimization
 */
export function useFormsTranslations() {
  // Use the default translation namespace since forms.json is merged into translation
  const { t } = useTranslation('translation');

  // Memoize translation functions to prevent re-renders
  const translations = useMemo(() => {
    // Helper functions for common translation patterns with type safety
    const getFormsText = (key: string, params?: Record<string, any>): string => {
      return String(t(`forms.${key}`, params || {}));
    };

    const getProductText = (key: string, params?: Record<string, any>): string => {
      return String(t(`forms.product.${key}`, params || {}));
    };

    const getCategoryText = (key: string, params?: Record<string, any>): string => {
      return String(t(`forms.category.${key}`, params || {}));
    };

    const getSearchText = (key: string, params?: Record<string, any>): string => {
      return String(t(`forms.search.${key}`, params || {}));
    };

    const getCommonText = (key: string, params?: Record<string, any>): string => {
      return String(t(`forms.common.${key}`, params || {}));
    };

    // Safe translation wrapper that always returns string
    const tSafe = (key: string, params?: Record<string, any>): string => {
      return String(t(key, params || {}));
    };

    return {
      // Direct translation function with type safety
      t: tSafe,

      // Category-specific helpers
      getFormsText,
      getProductText,
      getCategoryText,
      getSearchText,
      getCommonText,

      // Product form translations
      product: {
        form: {
          title: getProductText('form.title'),
          tabs: {
            basicInfo: getProductText('form.tabs.basicInfo'),
            description: getProductText('form.tabs.description'),
          },
          familyInfo: {
            label: getProductText('form.familyInfo.label'),
            none: getProductText('form.familyInfo.none'),
            helpSelected: getProductText('form.familyInfo.helpSelected'),
            helpGroupCount: (count: number, required: number) =>
              getProductText('form.familyInfo.helpGroupCount', {
                count,
                required,
              }),
            helpNoFamily: getProductText('form.familyInfo.helpNoFamily'),
            helpAdditional: getProductText('form.familyInfo.helpAdditional'),
          },
          description: {
            title: getProductText('form.description.title'),
            subtitle: getProductText('form.description.subtitle'),
          },
          categorySelect: {
            placeholder: getProductText('form.categorySelect.placeholder'),
          },
        },
        placeholders: {
          name: getProductText('placeholders.name'),
          sku: getProductText('placeholders.sku'),
          description: getProductText('placeholders.description'),
          selectStatus: getProductText('placeholders.selectStatus'),
          selectFamily: getProductText('placeholders.selectFamily'),
        },
        labels: {
          name: getProductText('labels.name'),
          sku: getProductText('labels.sku'),
          description: getProductText('labels.description'),
          status: getProductText('labels.status'),
          family: getProductText('labels.family'),
          category: getProductText('labels.category'),
        },
        buttons: {
          create: getProductText('buttons.create'),
          save: getProductText('buttons.save'),
          cancel: getProductText('buttons.cancel'),
          upload: getProductText('buttons.upload'),
          remove: getProductText('buttons.remove'),
        },
        status: {
          active: getProductText('status.active'),
          inactive: getProductText('status.inactive'),
          draft: getProductText('status.draft'),
        },
        validation: {
          nameRequired: getProductText('validation.nameRequired'),
          skuRequired: getProductText('validation.skuRequired'),
          skuUnique: getProductText('validation.skuUnique'),
          statusRequired: getProductText('validation.statusRequired'),
        },
        notifications: {
          createSuccess: getProductText('notifications.createSuccess'),
          updateSuccess: getProductText('notifications.updateSuccess'),
          createFailed: getProductText('notifications.createFailed'),
          updateFailed: getProductText('notifications.updateFailed'),
          statusUpdateSuccess: (status: string) =>
            getProductText('notifications.statusUpdateSuccess', { status }),
          statusUpdateFailed: getProductText('notifications.statusUpdateFailed'),
        },
      },

      // Category form translations
      category: {
        placeholders: {
          name: getCategoryText('placeholders.name'),
          search: getCategoryText('placeholders.search'),
          description: getCategoryText('placeholders.description'),
        },
        labels: {
          name: getCategoryText('labels.name'),
          description: getCategoryText('labels.description'),
          parent: getCategoryText('labels.parent'),
        },
        buttons: {
          create: getCategoryText('buttons.create'),
          save: getCategoryText('buttons.save'),
          cancel: getCategoryText('buttons.cancel'),
          select: getCategoryText('buttons.select'),
        },
        validation: {
          nameRequired: getCategoryText('validation.nameRequired'),
        },
        notifications: {
          createSuccess: getCategoryText('notifications.createSuccess'),
          updateSuccess: getCategoryText('notifications.updateSuccess'),
          createFailed: getCategoryText('notifications.createFailed'),
          updateFailed: getCategoryText('notifications.updateFailed'),
        },
      },

      // Search form translations
      search: {
        placeholders: {
          general: getSearchText('placeholders.general'),
          byNameCodeDescription: getSearchText('placeholders.byNameCodeDescription'),
          categories: getSearchText('placeholders.categories'),
          products: getSearchText('placeholders.products'),
          assets: getSearchText('placeholders.assets'),
        },
        buttons: {
          search: getSearchText('buttons.search'),
          clear: getSearchText('buttons.clear'),
          filter: getSearchText('buttons.filter'),
          reset: getSearchText('buttons.reset'),
        },
        labels: {
          searchQuery: getSearchText('labels.searchQuery'),
          filters: getSearchText('labels.filters'),
          results: getSearchText('labels.results'),
        },
        aria: {
          searchInput: getSearchText('aria.searchInput'),
          searchButton: getSearchText('aria.searchButton'),
          clearSearch: getSearchText('aria.clearSearch'),
          filterPanel: getSearchText('aria.filterPanel'),
        },
      },

      // Common form translations
      common: {
        placeholders: {
          select: getCommonText('placeholders.select'),
          selectMultiple: getCommonText('placeholders.selectMultiple'),
          noOptions: getCommonText('placeholders.noOptions'),
        },
        buttons: {
          add: getCommonText('buttons.add'),
          edit: getCommonText('buttons.edit'),
          delete: getCommonText('buttons.delete'),
          save: getCommonText('buttons.save'),
          cancel: getCommonText('buttons.cancel'),
          submit: getCommonText('buttons.submit'),
          reset: getCommonText('buttons.reset'),
          close: getCommonText('buttons.close'),
        },
        status: {
          active: getCommonText('status.active'),
          inactive: getCommonText('status.inactive'),
          enabled: getCommonText('status.enabled'),
          disabled: getCommonText('status.disabled'),
          draft: getCommonText('status.draft'),
          published: getCommonText('status.published'),
        },
        validation: {
          required: getCommonText('validation.required'),
          invalid: getCommonText('validation.invalid'),
          tooShort: getCommonText('validation.tooShort'),
          tooLong: getCommonText('validation.tooLong'),
          invalidEmail: getCommonText('validation.invalidEmail'),
          invalidNumber: getCommonText('validation.invalidNumber'),
        },
        aria: {
          required: getCommonText('aria.required'),
          optional: getCommonText('aria.optional'),
          invalid: getCommonText('aria.invalid'),
          loading: getCommonText('aria.loading'),
          saving: getCommonText('aria.saving'),
          saved: getCommonText('aria.saved'),
          toggleStatus: (status: string) => getCommonText('aria.toggleStatus', { status }),
          downloadAsset: getCommonText('aria.downloadAsset'),
          editField: (field: string) => getCommonText('aria.editField', { field }),
          saveField: (field: string) => getCommonText('aria.saveField', { field }),
          cancelEdit: (field: string) => getCommonText('aria.cancelEdit', { field }),
        },
        tooltips: {
          viewImage: getCommonText('tooltips.viewImage'),
          editField: (field: string) => getCommonText('tooltips.editField', { field }),
          required: getCommonText('tooltips.required'),
          optional: getCommonText('tooltips.optional'),
        },
        errors: {
          loadingCategories: getCommonText('errors.loadingCategories'),
          loadingFamilies: getCommonText('errors.loadingFamilies'),
          savingFailed: getCommonText('errors.savingFailed'),
          unexpected: getCommonText('errors.unexpected'),
        },
      },
    };
  }, [t]);

  return translations;
}

export type FormsTranslations = ReturnType<typeof useFormsTranslations>;
